import styled from '@emotion/styled';
import { Input } from 'antd';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { Topics } from 'src/modules/programs/components/Topics';
import * as S from 'src/modules/programs/stepComponents/styles';
import { color, font } from 'src/shared/styles/variables';

import {
    changeMindfulnessImprovementsAdditionalInfo,
    changeMindfulnessImprovementsTopic,
    submitMindfulnessImprovementsData,
} from '../actions/weekOneActions';
import {
    MindfulnessImprovementsTopicItem,
    MindfulnessImprovementsTopics as IMindfulnessImprovementsTopics,
} from '../models/weekOne';
import { mindfulnessImprovementsTopics } from '../models/weekOneData';
import {
    getHowDoYouWantUseMindfulnessData,
    isHowDoYouWantUseMindfulnessDataValid,
} from '../selectors/weekOneSelectors';

const Header = styled('h3')(
    {
        color: color.chathamsBlue,
    },
    font.heavy(font.size.medium),
);

const StyledInput = styled(Input)({
    marginTop: '1.8rem',
});

interface StateProps {
    data: IMindfulnessImprovementsTopics;
    isDataValid: boolean;
}

interface ActionsProps {
    changeMindfulnessImprovementsTopic: typeof changeMindfulnessImprovementsTopic;
    changeMindfulnessImprovementsAdditionalInfo: typeof changeMindfulnessImprovementsAdditionalInfo;
    submitMindfulnessImprovementsData: typeof submitMindfulnessImprovementsData;
}

type Props = StepButtonsProps & StateProps & ActionsProps & WithTranslation;

class MindfulnessImprovementsTopicsStepContainer extends React.Component<Props> {
    private handleTopicChange = (value: string, field: string) =>
        this.props.changeMindfulnessImprovementsTopic({ value, field });

    private handleWorkImprovementsTopicChange = (value: string) =>
        this.handleTopicChange(value, 'workImprovementsTopics');

    private handleLifeImprovementsTopicChange = (value: string) =>
        this.handleTopicChange(value, 'lifeImprovementsTopics');

    private handleAdditionalInfoChange = (value: string, field: string) =>
        this.props.changeMindfulnessImprovementsAdditionalInfo({ value, field });

    private handleLifeImprovementsAdditionalInfoChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.handleAdditionalInfoChange(e.target.value, 'lifeImprovementsAdditionalInfo');

    private handleWorkImprovementsAdditionalInfoChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.handleAdditionalInfoChange(e.target.value, 'workImprovementsAdditionalInfo');

    public getTopicsDefinitions = (topics: MindfulnessImprovementsTopicItem[]): string[] =>
        topics.reduce((definitions, topic) => [...definitions, topic.definition], []);

    private handleSubmit = () => {
        if (!this.props.isDataValid) {
            return;
        }

        this.props.submitMindfulnessImprovementsData(this.props.data);
        this.props.goToNextStep();
    };

    public render(): JSX.Element {
        const { data, t } = this.props;

        const {
            workImprovementsTopics,
            workImprovementsAdditionalInfo,
            lifeImprovementsAdditionalInfo,
            lifeImprovementsTopics,
        } = data;

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading>
                            {t('week-1.how-do-you-want-to-use-mindfulness.heading')}
                        </S.StepHeading>
                        <S.StepBoxWrapper>
                            <Header>
                                {t('week-1.how-do-you-want-to-use-mindfulness.questions.1')}
                            </Header>
                            <Topics
                                capitalize
                                topics={this.getTopicsDefinitions(
                                    mindfulnessImprovementsTopics.improveWorkTopics,
                                )}
                                selectedTopics={workImprovementsTopics}
                                onTopicChange={this.handleWorkImprovementsTopicChange}
                            />

                            {workImprovementsTopics.includes('Other') && (
                                <StyledInput
                                    value={workImprovementsAdditionalInfo}
                                    onChange={this.handleWorkImprovementsAdditionalInfoChange}
                                    size="large"
                                    placeholder={t(
                                        'week-1.how-do-you-want-to-use-mindfulness.placeholder',
                                    )}
                                />
                            )}
                        </S.StepBoxWrapper>
                        <S.StepBoxWrapper>
                            <Header>
                                {t('week-1.how-do-you-want-to-use-mindfulness.questions.2')}
                            </Header>
                            <Topics
                                capitalize
                                topics={this.getTopicsDefinitions(
                                    mindfulnessImprovementsTopics.improvePersonalLifeTopics,
                                )}
                                selectedTopics={lifeImprovementsTopics}
                                onTopicChange={this.handleLifeImprovementsTopicChange}
                            />

                            {lifeImprovementsTopics.includes('Other') && (
                                <StyledInput
                                    value={lifeImprovementsAdditionalInfo}
                                    onChange={this.handleLifeImprovementsAdditionalInfoChange}
                                    size="large"
                                    placeholder={t(
                                        'week-1.how-do-you-want-to-use-mindfulness.placeholder',
                                    )}
                                />
                            )}
                        </S.StepBoxWrapper>
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={this.props.goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!this.props.isDataValid}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    data: getHowDoYouWantUseMindfulnessData(state),
    isDataValid: isHowDoYouWantUseMindfulnessDataValid(state),
});

export const MindfulnessImprovementsTopicsStep = withTranslation('cp')(
    connect(mapStateToProps, {
        changeMindfulnessImprovementsTopic,
        changeMindfulnessImprovementsAdditionalInfo,
        submitMindfulnessImprovementsData,
    })(MindfulnessImprovementsTopicsStepContainer),
);
